import { Button, InputTextArea, InputNumber } from "@sellar-io/components";
import { useState } from "react";
import { useCollectionAddOrder } from "../../../hooks/api/v2/collections/useCollectionAddOrder";
import { InputLabel, InputContainer } from "@sellar-io/components";
import {
  Dialog,
  DialogNavigation,
  DialogContent,
  DialogCTA,
} from "../../modal/Dialog";
import { Switch } from "@sellar-io/components";
import { apiV2AssertError } from "../../../hooks/api/v2/apiV2Request";
import { Alert } from "../../alert/Alert";
import { useCollectionOrderData } from "../../../pages/collections/useCollectionOrderData";

export const AddOrderToCollectionDialog = ({
  active,
  onDismiss,
  orderId,
  collectionId,
}: {
  active: boolean;
  onDismiss: () => void;
  orderId: number;
  collectionId: number;
}) => {
  return (
    <Dialog active={active} onDismiss={onDismiss}>
      <DialogNavigation
        leftIcon={"CHEVRON_DOWN"}
        onClickLeftIcon={onDismiss}
        title={"Add order to collection"}
      />
      {active ? (
        <AddOrderDialogContent
          onDismiss={onDismiss}
          orderId={orderId}
          collectionId={collectionId}
        />
      ) : (
        <DialogContent className="min-h-[606px]">
          <></>
        </DialogContent>
      )}
    </Dialog>
  );
};

const AddOrderDialogContent = ({
  onDismiss,
  orderId,
  collectionId,
}: {
  onDismiss: () => void;
  orderId: number;
  collectionId: number;
}) => {
  const addOrderToCollectionMutation = useCollectionAddOrder(collectionId);

  const initialData = useCollectionOrderData(orderId);

  const [error, setError] = useState<string>();

  const [totalWeight, setTotalWeight] = useState(initialData.totalWeight);
  const [numberOfPackages, setNumberOfPackages] = useState(
    initialData.numberOfPackages,
  );
  const [specialInstructions, setSpecialInstructions] = useState("");
  const [isOverweight, setIsOverweight] = useState(initialData.overWeight);

  const addToCollection = () => {
    addOrderToCollectionMutation.mutate(
      {
        totalWeight,
        numberOfPackages,
        specialInstructions,
        isOverweight,
        orderId,
      },
      {
        onSuccess: onDismiss,
        onError: (error) => {
          setError(
            apiV2AssertError(error)?.response?.data.message ?? "Unknown error",
          );
        },
      },
    );
  };

  if (!collectionId) return;

  return (
    <>
      <DialogContent className={"flex flex-col gap-2"}>
        {!!error && (
          <Alert
            type="error"
            title="An error occured"
            message={error}
            className="mb-4"
          />
        )}
        <div className="flex flex-col gap-1">
          <InputLabel>* Total weight of all items (kg)</InputLabel>
          <InputContainer>
            <InputNumber
              required={true}
              value={totalWeight}
              onChange={(e) => setTotalWeight(parseInt(e.target.value))}
            />
          </InputContainer>
        </div>

        <div className="flex flex-col gap-1">
          <InputLabel>* Number of packages</InputLabel>
          <InputContainer>
            <InputNumber
              required={true}
              value={numberOfPackages}
              onChange={(e) => setNumberOfPackages(parseInt(e.target.value))}
            />
          </InputContainer>
        </div>

        <div className="flex flex-col gap-1">
          <InputLabel>Special instructions</InputLabel>
          <InputContainer>
            <InputTextArea
              variant="legacyFixed"
              value={specialInstructions}
              onChange={(e) => setSpecialInstructions(e.target.value)}
            />
          </InputContainer>
        </div>

        <div className="flex flex-col gap-1">
          <InputLabel>
            * Does this collection contain individual items over 32kg? (Such as
            steel kegs and casks)
          </InputLabel>
          <Switch
            checked={isOverweight}
            onChange={(checked) => setIsOverweight(checked)}
          />
        </div>
      </DialogContent>
      <DialogCTA className={"flex flex-col gap-2 md:flex-row"}>
        <>
          <Button
            className={"w-full md:w-auto"}
            onClick={addToCollection}
            loading={addOrderToCollectionMutation.isPending}
          >
            Add to collection
          </Button>
        </>
      </DialogCTA>
    </>
  );
};
